import { Badge, Box, Card, CardBody, FormLabel, Heading, HStack, Icon, IconButton, PopoverTrigger, Wrap, WrapItem } from '@chakra-ui/react';
import { CalendarIcon, CloseIcon } from '@chakra-ui/icons';
import { RangeDatepicker } from '../calendarPicker';
import { useIntl } from 'react-intl';
import React from 'react';
import { isSameDay } from 'date-fns';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { FilterChip } from '../attendance/AttendanceListFilter';
import { AbsenceStatus, AbsenceType } from '../../types';

type AbsenceListFilterProps = {
    onDateChange: (dates: Date[]) => void;
    selectedDates: Date[];
    selectedType?: AbsenceType;
    onTypeChange: (type?: AbsenceType) => void;
    selectedStatus?: AbsenceStatus;
    onStatusChange: (status?: AbsenceStatus) => void;
    totalRecords?: number;
    [key: string]: any;
};

export const AbsenceListFilter: React.FC<AbsenceListFilterProps> = ({
    onDateChange,
    selectedDates,
    selectedType,
    onTypeChange,
    selectedStatus,
    onStatusChange,
    totalRecords,
    ...restCardProps
}) => {
    const intl = useIntl();

    function isChipSelected(chipType: 'day' | 'week' | 'month'): boolean {
        let start = new Date();
        let end = new Date();
        switch (chipType) {
            case 'day':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                start.setHours(0, 0, 0, 0);
                start.setDate(start.getDate() - start.getDay());
                end.setHours(23, 59, 59, 999);
                end.setDate(start.getDate() + 6);
                break;
            case 'month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                break;
        }

        if (selectedDates.length === 2 && isSameDay(selectedDates[0], start) && isSameDay(selectedDates[1], end)) {
            return true;
        }

        return false;
    }

    return (
        <Card {...restCardProps} boxShadow={'md'}>
            <CardBody>
                <HStack mb={2} alignItems={'center'} justifyContent={'space-between'}>
                    <Heading alignItems={'center'} size={'md'}>
                        Filter
                    </Heading>
                    <Badge borderRadius={'md'} alignItems={'center'} display={'flex'}>
                        {totalRecords ?? 0}
                        <Icon ml={1} as={CalendarIcon} />
                    </Badge>
                </HStack>

                <FormLabel>{intl.formatMessage({ id: 'period' })}</FormLabel>
                <HStack>
                    <RangeDatepicker
                        selectedDates={selectedDates}
                        onDateChange={(dates) => {
                            onDateChange(dates);
                        }}
                        usePortal
                        configs={{
                            placeHolders: [intl.formatMessage({ id: 'dateFrom' }), intl.formatMessage({ id: 'dateTo' })],
                        }}
                    >
                        {(dates) => (
                            <PopoverTrigger>
                                <Box>
                                    <TimeRangeVisualizer
                                        interactive
                                        cursor={'pointer'}
                                        dateFrom={dates[0] ? intl.formatDate(dates[0]) : intl.formatMessage({ id: 'dateFrom' })}
                                        dateTo={dates[1] ? intl.formatDate(dates[1]) : intl.formatMessage({ id: 'dateTo' })}
                                    />
                                </Box>
                            </PopoverTrigger>
                        )}
                    </RangeDatepicker>

                    {selectedDates.length > 0 && (
                        <IconButton
                            onClick={() => onDateChange([])}
                            aria-label="clear dates"
                            size={'sm'}
                            icon={<CloseIcon color={'red.500'} />}
                            variant={'ghost'}
                        />
                    )}
                </HStack>
                <Wrap mt={2}>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.today' })}
                            onClear={() => onDateChange([])}
                            selected={isChipSelected('day')}
                            onSelect={() => {
                                const start = new Date();
                                const end = new Date();
                                end.setHours(23, 59, 59, 999);
                                start.setHours(0, 0, 0, 0);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.week' })}
                            onClear={() => onDateChange([])}
                            selected={isChipSelected('week')}
                            onSelect={() => {
                                const start = new Date();
                                start.setHours(0, 0, 0, 0);
                                start.setDate(start.getDate() - start.getDay());
                                const end = new Date();
                                end.setHours(23, 59, 59, 999);
                                end.setDate(start.getDate() + 6);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.month' })}
                            onClear={() => onDateChange([])}
                            selected={isChipSelected('month')}
                            onSelect={() => {
                                const start = new Date();
                                start.setDate(1);
                                start.setHours(0, 0, 0, 0);
                                const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
                                end.setHours(23, 59, 59, 999);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                </Wrap>

                <FormLabel mt={4}>{intl.formatMessage({ id: 'type' })}</FormLabel>
                <Wrap>
                    {Object.values(AbsenceType).map((type) => (
                        <WrapItem key={type}>
                            <FilterChip
                                colorScheme="gray"
                                label={intl.formatMessage({ id: `absence.type.${type}` })}
                                onClear={() => onTypeChange(undefined)}
                                selected={selectedType === type}
                                onSelect={() => onTypeChange(type)}
                            />
                        </WrapItem>
                    ))}
                </Wrap>

                <FormLabel mt={4}>{intl.formatMessage({ id: 'field.status' })}</FormLabel>
                <Wrap>
                    {Object.values(AbsenceStatus).map((status) => (
                        <WrapItem key={status}>
                            <FilterChip
                                colorScheme="gray"
                                label={intl.formatMessage({ id: `absence.status.${status}` })}
                                onClear={() => onStatusChange(undefined)}
                                selected={selectedStatus === status}
                                onSelect={() => onStatusChange(status)}
                            />
                        </WrapItem>
                    ))}
                </Wrap>
            </CardBody>
        </Card>
    );
};
